import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import axiosClient from "../../../../api-client/AxiosClient";
import { Trash2 } from "lucide-react";
import { FaEdit, FaCog } from "react-icons/fa";
import OrganizationConfigureModal from './OrganizationConfigureModal'



// Helper function for button hover handlers
const createHoverHandlers = (hoverColor, defaultColor, hoverTextColor = null, defaultTextColor = null) => ({
  onMouseOver: (e) => {
    e.currentTarget.style.backgroundColor = hoverColor;
    if (hoverTextColor) e.currentTarget.style.color = hoverTextColor;
  },
  onMouseOut: (e) => {
    e.currentTarget.style.backgroundColor = defaultColor;
    if (defaultTextColor) e.currentTarget.style.color = defaultTextColor;
  },
  onFocus: (e) => {
    e.currentTarget.style.backgroundColor = hoverColor;
    if (hoverTextColor) e.currentTarget.style.color = hoverTextColor;
  },
  onBlur: (e) => {
    e.currentTarget.style.backgroundColor = defaultColor;
    if (defaultTextColor) e.currentTarget.style.color = defaultTextColor;
  },
});

// Professional Modal Component
const OrgModal = ({ show, title, onClose, children }) => {
  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: "20px",
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          width: "100%",
          maxWidth: "600px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        }}
      >
        <div
          style={{
            padding: "24px 32px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "24px",
              fontWeight: "700",
              color: "#111827",
            }}
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "28px",
              cursor: "pointer",
              color: "#6b7280",
              lineHeight: 1,
              padding: 0,
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "6px",
              transition: "all 0.2s",
            }}
            {...createHoverHandlers("#f3f4f6", "transparent")}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>
        <div style={{ padding: "20px 24px", overflowY: "auto", flex: 1 }}>
          {children}
        </div>
      </div>
    </div>
  );
};

OrgModal.propTypes = {
  show: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

export default function OrganizationManagement() {
  const { OrganizationMgmt } = JSON.parse(
    sessionStorage.getItem("rolesAndPermission") || '{"OrganizationMgmt":{"canAdd":true,"canUpdate":true,"canDelete":true}}'
  );

  const [organizations, setOrganizations] = useState([]);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [newOrgId, setNewOrgId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [showConfigureModal, setShowConfigureModal] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [sortAsc, setSortAsc] = useState(true);
  const [page, setPage] = useState(1);
  const [editOrgId, setEditOrgId] = useState(null);
  const pageSize = 5;

  const [orgForm, setOrgForm] = useState({
    name: "",
    description: "",
    location: "",
    organizationType: "",
    isActive: "true",
  });

  const fieldStyle = { marginBottom: "16px" };
  const labelStyle = {
    display: "block",
    fontSize: "13px",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "6px",
  };
  const inputStyle = {
    width: "100%",
    padding: "11px 14px",
    fontSize: "14px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    outline: "none",
    transition: "all 0.2s",
    boxSizing: "border-box",
  };
  const inputFocusStyle = {
    borderColor: "#3b82f6",
    boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
  };

  const isFormValid = () => {
    return (
      orgForm.name.trim() !== "" &&
      orgForm.description.trim() !== "" &&
      orgForm.location.trim() !== "" &&
      orgForm.organizationType !== "" &&
      orgForm.isActive !== ""
    );
  };

  // Fetch Organizations
  const fetchOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosClient.get("/api/Organization/GetUserOrganizations");
      setOrganizations(response.data || []);
      setError(null);
    } catch (err) {
      const errorMessage = err?.response?.data?.message || "Failed to load organizations.";
      setError(errorMessage);
      toast.error(errorMessage);
      console.error("Error fetching organizations:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // Filter and sort
  const filteredOrgs = organizations.filter((org) =>
    org.name.toLowerCase().includes(search.toLowerCase())
  );

  const sortedOrgs = [...filteredOrgs].sort((a, b) => {
    const nameA = a.name.toLowerCase();
    const nameB = b.name.toLowerCase();
    if (nameA < nameB) return sortAsc ? -1 : 1;
    if (nameA > nameB) return sortAsc ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sortedOrgs.length / pageSize);
  const pagedOrgs = sortedOrgs.slice((page - 1) * pageSize, page * pageSize);

  // Handle Search
  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  // Handle Submit (Create/Update)
  const handleSubmit = async () => {
    if (!orgForm.name || !orgForm.location || !orgForm.organizationType) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setSaveLoading(true);
    try {
      const payload = {
        name: orgForm.name,
        description: orgForm.description,
        location: orgForm.location,
        organizationType: orgForm.organizationType,
        isActive: orgForm.isActive === "true",
        organizationId: editOrgId || "0",
      };

      if (editOrgId) {
        await axiosClient.put("/api/Organization/UpdateOrganization", payload);
        toast.success("Organization updated successfully.");
        setShowOrgModal(false);
        resetForm();
        fetchOrganizations();
      } else {
        const response = await axiosClient.post("/api/Organization", payload);
        toast.success("Organization created successfully.");
        setNewOrgId(response.data?.organizationId || response.data?.id);
        fetchOrganizations();
        setWizardStep(2);
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || "Failed to save organization.";
      toast.error(errorMessage);
      console.error("Error saving organization:", err);
    } finally {
      setSaveLoading(false);
    }
  };
  // Handle Delete
  const handleDelete = async () => {
    try {
      await axiosClient.delete(`/api/Organization/${editOrgId}`);
      toast.success("Organization deleted successfully.");
      setShowDeleteModal(false);
      fetchOrganizations();
    } catch (err) {
      const errorMessage = err?.response?.data?.message || "Failed to delete organization.";
      toast.error(errorMessage);
      console.error("Error deleting organization:", err);
    }
  };

  // Reset Form
  const resetForm = () => {
    setOrgForm({
      name: "",
      description: "",
      location: "",
      organizationType: "",
      isActive: "true",
    });
    setEditOrgId(null);
    setWizardStep(1);
    setNewOrgId(null);
  };

  // Render table content helper
  const renderTableContent = () => {
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: "48px", color: "#6b7280" }}>
          Loading organizations...
        </div>
      );
    }
    if (error) {
      return (
        <div style={{ textAlign: "center", padding: "48px", color: "#ef4444" }}>
          {error}
        </div>
      );
    }
    return renderOrganizationsTable();
  };

  // Render organizations table
  const renderOrganizationsTable = () => {
    const hasActions = OrganizationMgmt?.canUpdate || OrganizationMgmt?.canDelete;
    return (
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            backgroundColor: "white",
            borderRadius: "12px",
            border: "1px solid #e5e7eb",
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
        >
          <thead>
            <tr style={{ background: "linear-gradient(to right, #eff6ff, #dbeafe)" }}>
              <th
                onClick={() => setSortAsc(!sortAsc)}
                style={{
                  padding: "16px 20px",
                  textAlign: "left",
                  fontWeight: "700",
                  color: "#374151",
                  cursor: "pointer",
                  borderBottom: "2px solid #bfdbfe",
                }}
              >
                Organization Name{" "}
                <span style={{ marginLeft: "8px", fontSize: "12px" }}>
                  {sortAsc ? "▲" : "▼"}
                </span>
              </th>
              <th
                style={{
                  padding: "16px 20px",
                  textAlign: "left",
                  fontWeight: "700",
                  color: "#374151",
                  borderBottom: "2px solid #bfdbfe",
                }}
              >
                Location
              </th>
              <th
                style={{
                  padding: "16px 20px",
                  textAlign: "left",
                  fontWeight: "700",
                  color: "#374151",
                  borderBottom: "2px solid #bfdbfe",
                }}
              >
                Type
              </th>
              <th
                style={{
                  padding: "16px 20px",
                  textAlign: "left",
                  fontWeight: "700",
                  color: "#374151",
                  borderBottom: "2px solid #bfdbfe",
                }}
              >
                Status
              </th>
              <th
                style={{
                  padding: "16px 20px",
                  textAlign: "left",
                  fontWeight: "700",
                  color: "#374151",
                  borderBottom: "2px solid #bfdbfe",
                }}
              >
                Created Date
              </th>
              {hasActions && (
                <th
                  style={{
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: "700",
                    color: "#374151",
                    borderBottom: "2px solid #bfdbfe",
                  }}
                >
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {pagedOrgs.length === 0 ? (
              <tr>
                <td
                  colSpan={hasActions ? 6 : 5}
                  style={{
                    padding: "48px",
                    textAlign: "center",
                    color: "#9ca3af",
                  }}
                >
                  No organizations found.
                </td>
              </tr>
            ) : (
              pagedOrgs.map((org, idx) => (
                <tr
                  key={org.organizationId}
                  style={{
                    backgroundColor: idx % 2 === 0 ? "white" : "#f9fafb",
                    transition: "background-color 0.2s",
                  }}
                  {...createHoverHandlers("#eff6ff", idx % 2 === 0 ? "white" : "#f9fafb")}
                >
                  <td
                    style={{
                      padding: "16px 20px",
                      color: "#111827",
                      fontWeight: "500",
                      borderBottom:
                        idx === pagedOrgs.length - 1 ? "none" : "1px solid #e5e7eb",
                    }}
                  >
                    {org.name}
                  </td>
                  <td
                    style={{
                      padding: "16px 20px",
                      color: "#6b7280",
                      borderBottom:
                        idx === pagedOrgs.length - 1 ? "none" : "1px solid #e5e7eb",
                    }}
                  >
                    {org.location}
                  </td>
                  <td
                    style={{
                      padding: "16px 20px",
                      borderBottom:
                        idx === pagedOrgs.length - 1 ? "none" : "1px solid #e5e7eb",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        padding: "4px 12px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: "600",
                        backgroundColor: "#dbeafe",
                        color: "#1e40af",
                        border: "1px solid #bfdbfe",
                      }}
                    >
                      {org.organizationType}
                    </span>
                  </td>
                  <td
                    style={{
                      padding: "16px 20px",
                      borderBottom:
                        idx === pagedOrgs.length - 1 ? "none" : "1px solid #e5e7eb",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        padding: "4px 12px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: "600",
                        backgroundColor: org.isActive ? "#dcfce7" : "#fee2e2",
                        color: org.isActive ? "#16a34a" : "#dc2626",
                        border: `1px solid ${org.isActive ? "#bbf7d0" : "#fecaca"}`,
                      }}
                    >
                      {org.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td
                    style={{
                      padding: "16px 20px",
                      color: "#6b7280",
                      borderBottom:
                        idx === pagedOrgs.length - 1 ? "none" : "1px solid #e5e7eb",
                    }}
                  >
                    {new Date(org.createdOn).toLocaleDateString()}
                  </td>
                  {hasActions && (
                    <td
                      style={{
                        padding: "16px 20px",
                        borderBottom:
                          idx === pagedOrgs.length - 1 ? "none" : "1px solid #e5e7eb",
                      }}
                    >
                      <div style={{ display: "flex" }}>
                        {/* SETTINGS BUTTON  */}
                        <button
                          onClick={() => {
                            setSelectedOrganization(org);
                            setShowConfigureModal(true);
                          }}
                          style={{
                            padding: "8px",
                            borderRadius: "9999px",
                            border: "none",
                            backgroundColor: "transparent",
                            color: "#6b7280",
                            cursor: "pointer",
                            transition: "all 0.2s",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          {...createHoverHandlers("#f3f4f6", "transparent", "#4b5563", "#6b7280")}
                          title="Configure Organization"
                        >
                          <FaCog size={15} />
                        </button>
                        {OrganizationMgmt?.canUpdate && (
                          <button
                            onClick={() => {
                              setShowOrgModal(true);
                              setEditOrgId(org.organizationId);
                              setOrgForm({
                                name: org.name,
                                description: org.description || "",
                                location: org.location,
                                organizationType: org.organizationType,
                                isActive: org.isActive ? "true" : "false",
                              });
                            }}
                            style={{
                              padding: "8px",
                              borderRadius: "9999px",
                              border: "none",
                              backgroundColor: "transparent",
                              color: "#3b82f6",
                              cursor: "pointer",
                              transition: "all 0.2s",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                            {...createHoverHandlers("#dbeafe", "transparent", "#2563eb", "#3b82f6")}
                            title="Edit organization"
                          >
                            <FaEdit size={15} />
                          </button>
                        )}
                        {OrganizationMgmt?.canDelete && (
                          <button
                            onClick={() => {
                              setEditOrgId(org.organizationId);
                              setShowDeleteModal(true);
                            }}
                            style={{
                              padding: "8px",
                              borderRadius: "9999px",
                              border: "none",
                              backgroundColor: "transparent",
                              color: "#ef4444",
                              cursor: "pointer",
                              transition: "all 0.2s",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                            {...createHoverHandlers("#fee2e2", "transparent", "#dc2626", "#ef4444")}
                            title="Delete organization"
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div
      style={{
        borderRadius: "16px",
        backgroundColor: "white",
        padding: "24px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        marginTop: "24px",
      }}
    >
      <h2
        style={{
          fontSize: "28px",
          fontWeight: "700",
          marginBottom: "8px",
          color: "#111827",
        }}
      >
        Organization Management
      </h2>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "16px",
          marginBottom: "24px",
        }}
      >
        <input
          type="text"
          placeholder="Search by organization name..."
          value={search}
          onChange={handleSearch}
          style={{ ...inputStyle, width: "300px", minWidth: "250px" }}
          onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
          onBlur={(e) => {
            e.target.style.borderColor = "#d1d5db";
            e.target.style.boxShadow = "none";
          }}
        />
        {OrganizationMgmt?.canAdd && (
          <button
            onClick={() => {
              setShowOrgModal(true);
              resetForm();
            }}
            style={{
              backgroundColor: "#1e40af",
              color: "white",
              padding: "10px 20px",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "background-color 0.2s",
            }}
            {...createHoverHandlers("#1e3a8a", "#1e40af")}
          >
            <span style={{ fontSize: "20px", fontWeight: "700" }}>+</span> Add Organization
          </button>
        )}
      </div>

      {/* Add/Edit Modal */}
      <OrgModal
        show={showOrgModal && wizardStep === 1}
        title={editOrgId ? "Edit Organization" : "Add Organization"}
        onClose={() => { setShowOrgModal(false); resetForm(); }}
      >
        <div>
          {/* Step Indicator */}
          {!editOrgId && (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginBottom: "20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ width: "28px", height: "28px", borderRadius: "50%", backgroundColor: "#1e40af", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: "700" }}>1</div>
                <div style={{ width: "60px", height: "2px", backgroundColor: "#d1d5db" }}></div>
                <div style={{ width: "28px", height: "28px", borderRadius: "50%", backgroundColor: "#e5e7eb", color: "#9ca3af", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: "700" }}>2</div>
              </div>
              <span style={{ fontSize: "12px", color: "#6b7280", fontWeight: "500", marginLeft: "12px" }}>Step 1 of 2</span>
            </div>
          )}

          <div style={fieldStyle}>
            <label htmlFor="org-name" style={labelStyle}>
              Organization Name <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              id="org-name"
              type="text"
              placeholder="Enter organization name"
              value={orgForm.name}
              onChange={(e) => setOrgForm((f) => ({ ...f, name: e.target.value }))}
              style={inputStyle}
              onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
              onBlur={(e) => {
                e.target.style.borderColor = "#d1d5db";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>

          <div style={fieldStyle}>
            <label htmlFor="org-description" style={labelStyle}>
              Organization Description <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              id="org-description"
              type="text"
              placeholder="Enter description"
              value={orgForm.description}
              onChange={(e) => setOrgForm((f) => ({ ...f, description: e.target.value }))}
              style={inputStyle}
              onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
              onBlur={(e) => {
                e.target.style.borderColor = "#d1d5db";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>

          <div style={fieldStyle}>
            <label htmlFor="org-location" style={labelStyle}>
              Location <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              id="org-location"
              type="text"
              placeholder="Enter location"
              value={orgForm.location}
              onChange={(e) => setOrgForm((f) => ({ ...f, location: e.target.value }))}
              style={inputStyle}
              onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
              onBlur={(e) => {
                e.target.style.borderColor = "#d1d5db";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>

          <div style={fieldStyle}>
            <label htmlFor="org-type" style={labelStyle}>
              Organization Type <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <select
              id="org-type"
              value={orgForm.organizationType}
              onChange={(e) => setOrgForm((f) => ({ ...f, organizationType: e.target.value }))}
              style={{ ...inputStyle, cursor: "pointer" }}
              onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
              onBlur={(e) => {
                e.target.style.borderColor = "#d1d5db";
                e.target.style.boxShadow = "none";
              }}
            >
              <option value="">Select type</option>
              <option value="PoC">PoC</option>
              <option value="Intake">Intake</option>
              <option value="Production">Production</option>
            </select>
          </div>

          <div style={fieldStyle}>
            <label htmlFor="org-status" style={labelStyle}>
              Status <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <select
              id="org-status"
              value={orgForm.isActive}
              onChange={(e) => setOrgForm((f) => ({ ...f, isActive: e.target.value }))}
              style={{ ...inputStyle, cursor: "pointer" }}
              onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
              onBlur={(e) => {
                e.target.style.borderColor = "#d1d5db";
                e.target.style.boxShadow = "none";
              }}
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
              marginTop: "12px",
            }}
          >
            <button
              onClick={() => { setShowOrgModal(false); resetForm(); }}
              style={{
                padding: "10px 24px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                backgroundColor: "white",
                color: "#374151",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              {...createHoverHandlers("#f9fafb", "white")}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saveLoading || !isFormValid()}
              style={{
                padding: "10px 24px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: (saveLoading || !isFormValid()) ? "#9ca3af" : "#f97316",
                color: "white",
                fontSize: "14px",
                fontWeight: "600",
                cursor: (saveLoading || !isFormValid()) ? "not-allowed" : "pointer",
                transition: "background-color 0.2s",
              }}
              {...(!saveLoading && isFormValid() && createHoverHandlers("#ea580c", "#f97316"))}
            >
              {saveLoading ? "Saving..." : editOrgId ? "Save Organization" : "Save & Next →"}
            </button>
          </div>
        </div>
        

       </OrgModal>

      {/* Step 2: Configuration Form - rendered outside OrgModal */}
      {showOrgModal && wizardStep === 2 && (
        <OrganizationConfigureModal
          isOpen={true}
          isWizardMode={true}
          organization={{ organizationId: newOrgId, name: orgForm.name }}
          onClose={() => { 
            setShowOrgModal(false); 
            resetForm();
            fetchOrganizations(); // refresh table after wizard completes
          }}
          onBack={() => setWizardStep(1)}
        />
      )}

      {/* Delete Confirmation Modal */}
      <OrgModal
        show={showDeleteModal}
        title="Delete Organization"
        onClose={() => setShowDeleteModal(false)}
      >
        <div>
          <p style={{ fontSize: "16px", color: "#6b7280", marginBottom: "24px" }}>
            Are you sure you want to delete this organization?
          </p>
          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
            <button
              onClick={() => setShowDeleteModal(false)}
              style={{
                padding: "10px 24px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                backgroundColor: "white",
                color: "#374151",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              {...createHoverHandlers("#f9fafb", "white")}
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              style={{
                padding: "10px 24px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: "#ef4444",
                color: "white",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              {...createHoverHandlers("#dc2626", "#ef4444")}
            >
              Delete
            </button>
          </div>
        </div>
      </OrgModal>

      {/* Organization Configure Modal */}
      <OrganizationConfigureModal
        isOpen={showConfigureModal}
        organization={selectedOrganization}
        onClose={() => {
          setShowConfigureModal(false);
          setSelectedOrganization(null);
        }}
      />


      {/* Organizations Table */}
      {renderTableContent()}

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "12px",
            marginTop: "24px",
          }}
        >
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: "8px 16px",
              borderRadius: "8px",
              border: "1px solid #d1d5db",
              backgroundColor: page === 1 ? "#f9fafb" : "white",
              color: page === 1 ? "#9ca3af" : "#374151",
              fontSize: "14px",
              fontWeight: "600",
              cursor: page === 1 ? "not-allowed" : "pointer",
              transition: "all 0.2s",
            }}
            {...(page !== 1 && createHoverHandlers("#f3f4f6", "white"))}
          >
            Previous
          </button>
          <span style={{ fontSize: "14px", color: "#6b7280", fontWeight: "500" }}>
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{
              padding: "8px 16px",
              borderRadius: "8px",
              border: "1px solid #d1d5db",
              backgroundColor: page === totalPages ? "#f9fafb" : "white",
              color: page === totalPages ? "#9ca3af" : "#374151",
              fontSize: "14px",
              fontWeight: "600",
              cursor: page === totalPages ? "not-allowed" : "pointer",
              transition: "all 0.2s",
            }}
            {...(page !== totalPages && createHoverHandlers("#f3f4f6", "white"))}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}